import { useMemo } from "react";

import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { EMPTY_VALUE_PLACEHOLDER, formatShortWeekdayDate, formatTime } from "@shared";

import { AppointmentForTakeoverResponse } from "@/api";
import { AppointmentPurposeChip } from "@/common/components/appointments/shared/components/appointment-purpose-chip/AppointmentPurposeChip";
import { useLanguageContext } from "@/common/contexts/language-context/LanguageContext";
import { locales } from "@/common/contexts/localization-date-context/LocalizationDateContextProvider.utils";
import { useAppointmentLabels } from "@/common/hooks/useAppointmentLabels";
import { TakeoverView } from "@/pages/takeover/Takeover.types";
import { composeDetails, getFromUserCode, getTakenOverByUserCode } from "@/pages/takeover/Takeover.utils";
import { useTranslation } from "@/translations";

import { TakeoverColumnWidth } from "../TakeoverBoard.utils";
import { TakeoverActionCell } from "../components/TakeoverActionCell";

interface Params {
  view: TakeoverView;
  // undefined for a user without a counselor record of their own.
  currentCounselorId: string | undefined;
  isBusy: boolean;
  onTakeOver: (row: AppointmentForTakeoverResponse) => void;
  onReverse: (row: AppointmentForTakeoverResponse) => void;
  onMark: (row: AppointmentForTakeoverResponse) => void;
  onUnmark: (row: AppointmentForTakeoverResponse) => void;
}

/** Column definitions for the (flat) takeover board: ordered by date, with date, time, location and
 *  service sortable. */
export function useTakeoverColumns({
  view,
  currentCounselorId,
  isBusy,
  onTakeOver,
  onReverse,
  onMark,
  onUnmark,
}: Params): Array<GridColDef<AppointmentForTakeoverResponse>> {
  const { t } = useTranslation();
  const { language } = useLanguageContext();
  const labels = useAppointmentLabels();

  return useMemo<Array<GridColDef<AppointmentForTakeoverResponse>>>(
    () => [
      {
        field: "startTimeUtc",
        headerName: t("takeover.columns.date"),
        type: "date",
        flex: 1,
        maxWidth: TakeoverColumnWidth.date,
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) =>
          formatShortWeekdayDate(params.row.startTimeUtc, locales[language]),
      },
      {
        field: "time",
        headerName: t("takeover.columns.time"),
        flex: 1,
        maxWidth: TakeoverColumnWidth.time,
        // All-day appointments sort ahead of every timed one, as they do in the appointment exchange
        // (Tauschbörse).
        valueGetter: (_value, row) =>
          row.isAllDay ? -1 : row.startTimeUtc.getHours() * 60 + row.startTimeUtc.getMinutes(),
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) =>
          params.row.isAllDay
            ? t("takeover.allDay")
            : `${formatTime(params.row.startTimeUtc, locales[language])} ${t("common.clock")}`,
      },
      {
        field: "location",
        headerName: t("takeover.columns.location"),
        flex: 1,
        maxWidth: TakeoverColumnWidth.location,
        // Sorted by the label shown, not by the raw key behind it.
        valueGetter: (_value, row) => row.location ?? "",
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) =>
          params.row.location ?? EMPTY_VALUE_PLACEHOLDER,
      },
      {
        field: "purposeKey",
        headerName: t("takeover.columns.service"),
        flex: 1,
        maxWidth: TakeoverColumnWidth.service,
        valueGetter: (_value, row) => labels.purposeLabelOf(row.purposeKey),
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) => (
          <AppointmentPurposeChip label={labels.purposeLabelOf(params.row.purposeKey)} />
        ),
      },
      {
        field: "details",
        headerName: t("takeover.columns.details"),
        flex: 2,
        minWidth: TakeoverColumnWidth.details,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) =>
          composeDetails(params.row, labels),
      },
      {
        field: "from",
        headerName: t("takeover.columns.from"),
        flex: 1,
        maxWidth: TakeoverColumnWidth.from,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) => getFromUserCode(params.row),
      },
      {
        field: "takenOverBy",
        headerName: t("takeover.columns.takenOverBy"),
        flex: 1,
        maxWidth: TakeoverColumnWidth.takenOverBy,
        sortable: false,
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) =>
          getTakenOverByUserCode(params.row) ?? EMPTY_VALUE_PLACEHOLDER,
      },
      {
        field: "action",
        headerName: t("takeover.columns.action"),
        flex: 1,
        minWidth: TakeoverColumnWidth.action,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<AppointmentForTakeoverResponse>) => (
          <TakeoverActionCell
            row={params.row}
            view={view}
            currentCounselorId={currentCounselorId}
            isBusy={isBusy}
            onTakeOver={onTakeOver}
            onReverse={onReverse}
            onMark={onMark}
            onUnmark={onUnmark}
          />
        ),
      },
    ],
    [t, language, labels, view, currentCounselorId, isBusy, onTakeOver, onReverse, onMark, onUnmark]
  );
}
